import { useEffect, useState } from 'react';
import type { ChangeEvent } from 'react';
import { normalizeDate } from '@/helpers/normalizeDate';

const LABEL_CLASSES = "block text-[15px] font-semibold text-text mb-2.5 leading-normal pr-[50px] pl-[5px]";
const INPUT_CLASSES = "block w-full h-auto py-[14px] px-5 bg-background2 text-sm font-medium tracking-[0.02em] text-text rounded-md border border-gray-300 transition-all duration-500 ease-in-out box-border focus:outline-none focus:border-primary1 focus:ring-2 focus:ring-primary1/20 placeholder:text-gray-400";
const SELECT_CLASSES = "block w-full h-auto py-[14px] px-5 bg-background2 text-sm font-medium tracking-[0.02em] text-text rounded-md border border-gray-300 transition-all duration-500 ease-in-out box-border focus:outline-none focus:border-primary1 focus:ring-2 focus:ring-primary1/20 appearance-none";
const TEXTAREA_CLASSES = "block w-full h-auto py-[14px] px-5 bg-background2 text-sm font-medium tracking-[0.02em] text-text rounded-md border border-gray-300 transition-all duration-500 ease-in-out box-border focus:outline-none focus:border-primary1 focus:ring-2 focus:ring-primary1/20 placeholder:text-gray-400 resize-none";

type FormFieldProps = {
  label: string
  name?: string
  action?: (value: string) => void
  placeholder? : string
  readOnly?: boolean
  required?: boolean
  onChange?: (value: string | null) => void
  value?: string
  disabled?: boolean
  maxLength?: number
}

type NormalFormSelectionFieldProps<T> = {
  label: string;
  name: string;
  options: { name: string; value: T }[];
  action?: (value: T) => void;
  required?: boolean;
  noValueText?: string;
  onChange?: (value: T) => void;
  readOnly?: boolean;
  value?: T | null;
};

/* 
type NormalAutoCompleteSearchSelectionFieldProps = FormFieldProps & {
  options: { name: string, value: string }[]
}
*/

export const NormalFormEmailField = ({
  label,
  name,
  placeholder,
  action,
  required,
  readOnly,
  value: propValue,
  onChange: propOnChange,
  maxLength,
}: FormFieldProps) => {
  const [internalValue, setInternalValue] = useState<string>(propValue ?? '')

  useEffect(() => {
    setInternalValue(propValue ?? '')
  }, [propValue])

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    setInternalValue(newValue)
    propOnChange?.(newValue)
  }

  const handleBlur = () => {
    action?.(internalValue)
  }

  return (
    <div className="mb-[15px]">
      <label htmlFor={name} className={LABEL_CLASSES}>
        {
          required
          ? (
            `${label} *`
          )
          : label  
        }
      </label>    
      <input
        type="email"
        id={name}
        name={name}
        placeholder={placeholder}
        className={INPUT_CLASSES}
        value={internalValue}
        onChange={handleChange}
        required={required}
        onBlur={handleBlur}
        readOnly={readOnly}
        maxLength={maxLength}
      />
    </div>
  )
};

export const NormalFormPasswordField = ({
  label,
  name,
  placeholder,
  action,
  required,
  readOnly,
  value: propValue = '',
  onChange: propOnChange,
}: FormFieldProps) => {
  const [internalValue, setInternalValue] = useState<string>(propValue);

  useEffect(() => {
    setInternalValue(propValue)
  }, [propValue])

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInternalValue(newValue);
    if (newValue.length > 0) propOnChange?.(newValue)
    else propOnChange?.(null)
  };

  const handleBlur = () => {
    action?.(internalValue);
  };

  return (
    <div className="mb-[15px]">
      <label htmlFor={name} className={LABEL_CLASSES}>{required ? `${label} *` : label}</label>
      <input
        type="password"
        id={name}
        name={name}
        placeholder={placeholder}
        className={INPUT_CLASSES}
        value={internalValue}
        onChange={handleChange}
        required={required}
        onBlur={handleBlur}
        readOnly={readOnly}
      />
    </div>
  );
};

export const NormalFormTextField = ({
  label,
  name,
  placeholder,
  action,
  required,
  readOnly,
  value: propValue = '',
  onChange: propOnChange,
  maxLength,
}: FormFieldProps) => {
  const [internalValue, setInternalValue] = useState<string>(propValue);

  useEffect(() => {
    setInternalValue(propValue)
  }, [propValue])

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInternalValue(newValue);
    if (newValue.length > 0) propOnChange?.(newValue)
    else propOnChange?.(null)
  };

  const handleBlur = () => {
    action?.(internalValue);
  };

  return (
    <div className="mb-[15px]">
      <label htmlFor={name} className={LABEL_CLASSES}>{required ? `${label} *` : label}</label>
      <input
        type="text"
        id={name}
        name={name}
        placeholder={placeholder}
        className={INPUT_CLASSES}
        value={internalValue}
        onChange={handleChange}
        required={required}
        onBlur={handleBlur}
        readOnly={readOnly}
        maxLength={maxLength ?? 250}
      />
    </div>
  );
};

export const NormalFormTextArea = ({
  label,
  name,
  placeholder,
  action,
  required,
  value: propValue = '',
  onChange: propOnChange,
}: FormFieldProps) => {
  const [internalValue, setInternalValue] = useState<string>(propValue);

  useEffect(() => {
    setInternalValue(propValue)
  }, [propValue])

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    setInternalValue(newValue);
    if (newValue.length > 0) propOnChange?.(newValue)
    else propOnChange?.(null)
  };

  const handleBlur = () => {
    action?.(internalValue);
  };

  return (
    <div className="mb-[15px]">
      <label htmlFor={name} className={LABEL_CLASSES}>{required ? `${label} *` : label}</label>
      <textarea
        id={name}
        name={name}
        placeholder={placeholder}
        className={TEXTAREA_CLASSES}
        value={internalValue}
        onChange={handleChange}
        required={required}
        onBlur={handleBlur}
        rows={8}
      />
    </div>
  );
};

const GenericSelectionField = <T,>({
  label,
  name,
  options,
  required,
  noValueText = '---',
  onChange: propOnchange,
  readOnly,
  value: propValue
}: NormalFormSelectionFieldProps<T>) => {
  const [internalValue, setInternalValue] = useState<T | null>(propValue ?? null);

  const handleChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const selectedOption = options.find(opt => 
      String(opt.value) === e.target.value
    );
    if (selectedOption) {
      setInternalValue(selectedOption.value);
      propOnchange?.(selectedOption.value);
    }
  };

  useEffect(() => {
    setInternalValue(propValue ?? null)
  }, [propValue])

  return (
    <div className="mb-[15px]">
      <label htmlFor={name} className={LABEL_CLASSES}>{required ? `${label} *` : label}</label>
      <select
        id={name}
        name={name}
        className={SELECT_CLASSES}
        value={internalValue !== null ? String(internalValue) : ''}
        onChange={handleChange}
        required={required}
        disabled={readOnly}
      >
        <option value="">{noValueText}</option>
        {options.map((option, key) => (
          <option value={String(option.value)} key={key}>
            {option.name}
          </option>
        ))}
      </select>
    </div>
  );
};

export const NormalFormSelectionField: <T>(props: NormalFormSelectionFieldProps<T>) => React.ReactElement = GenericSelectionField

export const NormalFormDateField = ({
  label,
  name,
  placeholder,
  required,
  readOnly,
  value: propValue = '',
  onChange: propOnChange,
  disabled,
}: FormFieldProps) => {
  const [internalValue, setInternalValue] = useState<string>(propValue);
  const [hasChanged, setHasChanged] = useState(false)

  useEffect(() => {
    if (!hasChanged && propValue) setInternalValue(normalizeDate(propValue));
  }, [propValue, hasChanged]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInternalValue(newValue);
    propOnChange?.(newValue ?? '')
    setHasChanged(true)
  };

  return (
    <div className="mb-[15px]">
      <label htmlFor={name} className={LABEL_CLASSES}>{required ? `${label} *` : label}</label>
      <input
        type="date"
        id={name}
        name={name}
        placeholder={placeholder}
        className={INPUT_CLASSES}
        value={internalValue}
        onChange={handleChange}
        required={required}
        readOnly={readOnly}
        disabled={disabled}
      />
    </div>
  );
};

export const NormalFormNumberField = ({
  label,
  name,
  action,
  required,
  readOnly,
  value: propValue = '0',
  onChange: propOnChange,
}: Omit<FormFieldProps, 'placeholder'>) => {
  const [internalValue, setInternalValue] = useState<string>(propValue);

  useEffect(() => {
    setInternalValue(propValue);
  }, [propValue]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    
    setInternalValue(newValue);
    
    if (newValue.length > 0) propOnChange?.(newValue)
    else propOnChange?.('0')
  };

  const handleBlur = () => {
    action?.(internalValue);
  };

  return (
    <div className="mb-[15px]">
      <label htmlFor={name} className={LABEL_CLASSES}>{required ? `${label} *` : label}</label>
      <input
        type="number"
        id={name}
        name={name}
        className={INPUT_CLASSES}
        value={internalValue}
        onChange={handleChange}
        required={required}
        onBlur={handleBlur}
        readOnly={readOnly}
      />
    </div>
  );
};